import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  ArrowRight,
  Hammer,
  Building2,
  HomeIcon,
  Landmark,
  Wallet,
  HandCoins,
  Briefcase,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  CheckCircle2,
  Plus,
  Minus,
  Zap,
  Calculator,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_LINKS = [
  { href: "#products", label: "Loan Products" },
  { href: "#commercial", label: "Commercial" },
  { href: "#process", label: "Process" },
  { href: "#testimonials", label: "Reviews" },
  { href: "#faqs", label: "FAQs" },
  { href: "/brokers", label: "Brokers", external: true },
];

const PRODUCTS = [
  {
    id: "fix-flip",
    name: "Fix & Flip",
    Icon: Hammer,
    blurb:
      "Acquisition + rehab capital for flippers. Up to 90% of purchase and 100% of renovation, draws released fast so your crew never waits.",
    points: ["Up to 90% LTC", "100% of rehab", "12–24 month terms"],
  },
  {
    id: "dscr",
    name: "DSCR Rental",
    Icon: HomeIcon,
    blurb:
      "30-year financing for buy-and-hold investors. Qualify on the property's rent, not your W-2. Single-family, 2–4 unit, and short-term rentals.",
    points: ["No income docs", "30-yr fixed available", "Cash-out refi friendly"],
  },
  {
    id: "new-construction",
    name: "New Construction",
    Icon: Building2,
    blurb:
      "Ground-up financing for builders and developers. Lot, vertical, and infrastructure dollars released against a real schedule of values.",
    points: ["Up to 100% of build", "Spec & infill", "Experienced & new builders"],
  },
  {
    id: "bridge",
    name: "Bridge",
    Icon: Landmark,
    blurb:
      "Short-term capital between deals. Use it to acquire, season, stabilize, or refinance into long-term debt without losing the property.",
    points: ["6–24 month terms", "Interest-only", "Cross-collateral options"],
  },
  {
    id: "transactional",
    name: "Transactional",
    Icon: Wallet,
    blurb:
      "Same-day A→B→C funding for wholesalers and double closers. Bring the contracts and the assignment — we'll bring the cash to the table.",
    points: ["1–3 day funding", "No credit minimums", "Title-driven approval"],
  },
  {
    id: "emd",
    name: "EMD Funding",
    Icon: HandCoins,
    blurb:
      "Earnest money deposits funded in hours so you can lock up more contracts without tying up your own capital across multiple escrows.",
    points: ["Funded in hours", "Flat-fee pricing", "No appraisal needed"],
  },
  {
    id: "commercial",
    name: "Commercial",
    Icon: Briefcase,
    blurb:
      "Capital for mixed-use, multifamily 5+, retail, industrial, and small-balance commercial deals. Acquisition, value-add, and bridge-to-perm.",
    points: ["Up to $25M+", "Multifamily & mixed-use", "Value-add programs"],
  },
];

const WHO_WE_SERVE = [
  { title: "Fix & Flip Investors", desc: "Active operators who need rehab capital and a lender that can keep up with their pace." },
  { title: "Wholesalers", desc: "Assignors and double-closers funded with EMD and transactional capital — same day when needed." },
  { title: "New Construction Builders", desc: "Spec builders, infill developers, and small-balance ground-up projects nationwide." },
  { title: "Buy-and-Hold Landlords", desc: "DSCR 30-year financing for single-family, 2–4 unit, and short-term rental portfolios." },
  { title: "Commercial & Multifamily Investors", desc: "5+ unit multifamily, mixed-use, retail and small-balance commercial real estate." },
];

const PROCESS_STEPS = [
  { n: "01", title: "Apply in minutes", desc: "Submit your deal through our short intake form — no soft pull, no commitment." },
  { n: "02", title: "Term sheet in 24 hours", desc: "We underwrite the deal, not just the borrower. You'll have indicative terms within one business day." },
  { n: "03", title: "Underwriting & docs", desc: "Title, valuation, and docs run in parallel. Dedicated processor keeps every party moving." },
  { n: "04", title: "Close in 7–10 days", desc: "Sign, fund, and start the project. Future deals close even faster once you're in our system." },
];

const FAQS = [
  {
    q: "Where does GroundUp Lending operate?",
    a: "We lend nationwide across all 50 U.S. states. Our underwriting and processing team is built to handle multi-state portfolios for active investors.",
  },
  {
    q: "How fast can you close?",
    a: "Standard close is 7–10 business days from a signed term sheet. EMD and transactional funding can close in as little as a few hours.",
  },
  {
    q: "Do you really offer up to 100% financing?",
    a: "Yes — depending on the deal. On fix & flip projects we lend up to 90% of purchase and 100% of rehab. On new construction, qualified builders can access up to 100% of build cost. Every deal is underwritten on its own merits.",
  },
  {
    q: "Do I need previous investment experience?",
    a: "No. We work with first-time investors as well as seasoned operators. New investors may have slightly different leverage and reserve requirements, but a strong deal and a clear business plan can absolutely get funded.",
  },
  {
    q: "What credit score do you require?",
    a: "We're an asset-based lender. We typically look for a 660+ FICO for most programs, but lower scores can be accommodated on the right deal. DSCR and commercial programs have their own thresholds.",
  },
  {
    q: "What documents do I need to apply?",
    a: "To get a term sheet we usually need a short application, the purchase contract (if under contract), and a scope of work for rehab deals. Income docs are not required for DSCR or asset-based products.",
  },
];

const parseMoney = (str) => {
  if (!str) return 0;
  const n = Number(String(str).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const QuickQuote = ({ onLockTerms }) => {
  const [purchase, setPurchase] = useState("250000");
  const [rehab, setRehab] = useState("75000");
  const [arv, setArv] = useState("450000");

  const p = parseMoney(purchase);
  const r = parseMoney(rehab);
  const a = parseMoney(arv);

  // Max loan = min(90% purchase + 100% rehab, 75% ARV)
  const ltcCap = 0.9 * p + 1.0 * r;
  const ltarvCap = 0.75 * a;
  const maxLoan = Math.max(0, Math.min(ltcCap, ltarvCap));
  const totalProject = p + r;
  const cashToClose = Math.max(0, totalProject - maxLoan);
  const leverage = totalProject > 0 ? Math.round((maxLoan / totalProject) * 100) : 0;
  const bindingConstraint = ltarvCap < ltcCap ? "75% of ARV" : "90% purchase + 100% rehab";

  const handleLock = () => {
    onLockTerms(maxLoan > 0 ? formatMoney(maxLoan) : "");
  };

  const InputRow = ({ label, value, onChange, testid }) => (
    <div className="flex items-center justify-between gap-4 border-b border-neutral-800 py-3">
      <label className="label-overline text-neutral-400 whitespace-nowrap">{label}</label>
      <div className="flex items-center gap-1 text-right">
        <span className="text-neutral-500 text-lg">$</span>
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-transparent border-0 outline-none text-right font-heading text-2xl font-semibold text-white w-32 focus:text-[#22C55E] transition-colors"
          data-testid={testid}
        />
      </div>
    </div>
  );

  return (
    <div
      className="border border-neutral-800 bg-[#0F0F0F] p-6 md:p-8 relative overflow-hidden"
      data-testid="quick-quote-widget"
    >
      <div className="absolute top-0 right-0 bg-[#22C55E] text-white px-3 py-1 label-overline">
        <Zap size={11} className="inline mr-1 -mt-0.5" />
        Instant Quote
      </div>

      <div className="flex items-center gap-3 mb-6 mt-2">
        <Calculator size={18} className="text-[#22C55E]" />
        <h3 className="font-heading text-xl font-bold text-white">Quick Quote</h3>
      </div>

      <div className="mb-6">
        <InputRow label="Purchase Price" value={purchase} onChange={setPurchase} testid="quote-purchase" />
        <InputRow label="Rehab Budget" value={rehab} onChange={setRehab} testid="quote-rehab" />
        <InputRow label="ARV" value={arv} onChange={setArv} testid="quote-arv" />
      </div>

      <div className="bg-[#22C55E] -mx-6 md:-mx-8 px-6 md:px-8 py-5">
        <div className="label-overline text-white/80 mb-1">Estimated Max Loan</div>
        <div className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight" data-testid="quote-max-loan">
          {formatMoney(maxLoan)}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px bg-neutral-800 mt-px">
        <div className="bg-[#0F0F0F] p-4">
          <div className="label-overline text-neutral-500 mb-1">Cash to Close</div>
          <div className="font-heading text-xl font-bold text-white" data-testid="quote-cash-to-close">
            {formatMoney(cashToClose)}
          </div>
        </div>
        <div className="bg-[#0F0F0F] p-4">
          <div className="label-overline text-neutral-500 mb-1">Leverage</div>
          <div className="font-heading text-xl font-bold text-white" data-testid="quote-leverage">
            {leverage}%
          </div>
        </div>
      </div>

      <p className="text-[11px] text-neutral-500 mt-4 leading-relaxed font-mono">
        Constraint: {bindingConstraint}. Indicative only — final terms based on full underwriting.
      </p>

      <button
        onClick={handleLock}
        className="btn-primary w-full justify-center mt-5"
        data-testid="quote-lock-cta"
      >
        Lock These Terms <ArrowUpRight size={18} />
      </button>
    </div>
  );
};

const Nav = ({ onCtaClick }) => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 left-0 right-0 z-50 nav-blur" data-testid="site-nav">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[72px]">
        <a href="#top" className="flex items-center gap-2" data-testid="nav-logo">
          <img src="/brand/groundup-logo.png" alt="GroundUp Lending" className="h-9 md:h-10 w-auto" />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={`text-sm font-medium ${l.external ? "text-[#22C55E]" : "text-neutral-700"} hover:text-[#22C55E] transition-colors`}
              data-testid={`nav-link-${l.href.replace(/[#/]/g, "")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          <button onClick={onCtaClick} className="btn-primary" data-testid="nav-cta-apply">
            Apply Now <ArrowRight size={16} />
          </button>
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-neutral-200 bg-white" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-base font-medium">
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                onCtaClick();
              }}
              className="btn-primary w-full justify-center"
              data-testid="nav-mobile-cta"
            >
              Apply Now <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = ({ onCtaClick, onLockTerms }) => (
  <section id="top" className="relative pt-[72px] bg-[#0A0A0A] text-white overflow-hidden">
    <div
      className="absolute inset-0 opacity-70"
      style={{
        backgroundImage:
          "url('https://images.pexels.com/photos/13507175/pexels-photo-13507175.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/65 to-[#0A0A0A]/35" />
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
    <div className="absolute inset-0 bg-grid opacity-30" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="label-overline text-[#22C55E] mb-6 flex items-center gap-3" data-testid="hero-overline">
            <span className="inline-block w-8 h-px bg-[#22C55E]" />
            Nationwide Private Lending · Est. 2026
          </div>
          <h1
            className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-[80px] font-bold leading-[0.95] tracking-tight"
            data-testid="hero-headline"
          >
            Capital that closes
            <br />
            <span className="text-[#22C55E]">in 7–10 days.</span>
            <br />
            Not 7–10 weeks.
          </h1>
          <p
            className="mt-8 font-heading text-2xl md:text-3xl font-semibold leading-snug tracking-tight max-w-2xl"
            data-testid="hero-tagline"
          >
            If you're ready to buy, <span className="text-[#22C55E]">we're ready to lend.</span>
            <br />
            <span className="text-neutral-400 font-medium">Built from the ground up.</span>
          </p>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-neutral-300 leading-relaxed" data-testid="hero-sub">
            GroundUp Lending is a nationwide hard money and private lender built for real estate investors who move fast.
            Up to 100% financing on the right deal, flexible terms, and an underwriting team that actually picks up the phone.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button onClick={onCtaClick} className="btn-primary" data-testid="hero-cta-apply">
              Apply Now <ArrowUpRight size={18} />
            </button>
            <a href="#products" className="btn-ghost" data-testid="hero-cta-products">
              <span>See Loan Products</span>
              <ArrowRight size={18} />
            </a>
          </div>
          <div className="mt-12 flex flex-wrap gap-x-10 gap-y-4 border-t border-neutral-800 pt-8">
            {[
              { k: "7–10", v: "Days to Close" },
              { k: "100%", v: "Rehab Financing" },
              { k: "50", v: "States Funded" },
              { k: "$25M+", v: "Loan Capacity" },
            ].map((s) => (
              <div key={s.v} className="flex items-baseline gap-2">
                <div className="font-heading text-2xl md:text-3xl font-bold text-white">{s.k}</div>
                <div className="label-overline text-neutral-400">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <QuickQuote onLockTerms={onLockTerms} />
        </div>
      </div>
    </div>
  </section>
);

const TrustMarquee = () => {
  const items = [
    "Fix & Flip",
    "DSCR Rental",
    "New Construction",
    "Bridge Loans",
    "Transactional Funding",
    "EMD Funding",
    "Commercial Real Estate",
    "Nationwide Lending",
    "Close in 7–10 Days",
  ];
  const doubled = [...items, ...items];
  return (
    <section className="bg-[#0A0A0A] text-white border-t border-neutral-800" data-testid="trust-marquee">
      <div className="overflow-hidden">
        <div className="marquee-track flex whitespace-nowrap py-6">
          {doubled.map((t, i) => (
            <span key={i} className="px-10 label-overline text-neutral-400 flex items-center gap-10">
              {t} <span className="w-2 h-2 bg-[#22C55E] inline-block" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

const SectionLabel = ({ children, dark = false }) => (
  <div className={`label-overline mb-5 flex items-center gap-3 ${dark ? "text-neutral-400" : "text-neutral-500"}`}>
    <span className={`inline-block w-8 h-px ${dark ? "bg-neutral-500" : "bg-neutral-400"}`} />
    {children}
  </div>
);

const Products = ({ onCtaClick }) => (
  <section id="products" className="bg-[#F4F4F0] py-24 md:py-32" data-testid="products-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
        <div className="md:col-span-7">
          <SectionLabel>01 — Loan Products</SectionLabel>
          <h2 className="font-heading text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight">
            Seven products.
            <br />
            One lender. Every deal type.
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-lg text-neutral-600 leading-relaxed">
            From a single fix & flip to a 50-unit value-add multifamily, GroundUp has the program — and the speed — to make the deal work.
            Every product is underwritten in-house with one decision-maker on your file.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-neutral-300">
        {PRODUCTS.map((p, i) => (
          <div
            key={p.id}
            className="product-card p-8 md:p-10 border-r border-b border-neutral-300 bg-white min-h-[340px] flex flex-col"
            data-testid={`product-card-${p.id}`}
          >
            <div className="flex items-start justify-between mb-8">
              <div className="w-12 h-12 bg-[#22C55E] flex items-center justify-center">
                <p.Icon size={22} className="text-white" />
              </div>
              <ArrowUpRight size={22} className="product-arrow" />
            </div>
            <div className="label-overline text-neutral-500 mb-2">0{i + 1}</div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold mb-4">{p.name}</h3>
            <p className="text-[15px] leading-relaxed mb-6 opacity-80">{p.blurb}</p>
            <ul className="mt-auto space-y-2 pt-4 border-t border-current/20">
              {p.points.map((pt) => (
                <li key={pt} className="text-sm flex items-center gap-2 opacity-90">
                  <span className="w-1.5 h-1.5 bg-[#22C55E]" /> {pt}
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div className="p-8 md:p-10 border-r border-b border-neutral-300 bg-[#0A0A0A] text-white flex flex-col justify-between min-h-[340px]">
          <div>
            <div className="label-overline text-[#22C55E] mb-6">Not sure which fits?</div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold leading-tight">
              Talk to a real underwriter — not a chatbot.
            </h3>
          </div>
          <button onClick={onCtaClick} className="btn-primary mt-8 self-start" data-testid="products-cta">
            Apply Now <ArrowUpRight size={18} />
          </button>
        </div>
      </div>
    </div>
  </section>
);

const Commercial = ({ onCtaClick }) => {
  const subtypes = [
    { name: "Multifamily 5+", desc: "Stabilized and value-add apartment communities, 5–200+ units." },
    { name: "Mixed-Use", desc: "Ground-floor retail with residential above. Major metros and growing secondary markets." },
    { name: "Retail & Strip Centers", desc: "Anchored and unanchored retail, single-tenant NNN, and small shopping centers." },
    { name: "Industrial & Flex", desc: "Warehouse, light industrial, flex space, and small-bay industrial parks." },
    { name: "Office (Select)", desc: "Medical office, owner-user, and small-balance professional office in strong submarkets." },
    { name: "Self-Storage & Specialty", desc: "Self-storage, automotive, and other specialty commercial assets case-by-case." },
  ];
  const terms = [
    { k: "$500K – $25M+", v: "Loan Size" },
    { k: "Up to 75%", v: "LTV / LTC" },
    { k: "6 – 36 mo", v: "Bridge Terms" },
    { k: "30 yr fixed", v: "Perm Options" },
  ];
  return (
    <section id="commercial" className="bg-[#2C3E6D] text-white py-24 md:py-32" data-testid="commercial-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="label-overline mb-5 flex items-center gap-3 text-[#22C55E]">
              <span className="inline-block w-8 h-px bg-[#22C55E]" />
              02 — Commercial Lending
            </div>
            <h2 className="font-heading text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight">
              Commercial deals,
              <br />
              <span className="text-[#22C55E]">underwritten like a partner.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-lg text-white/80 leading-relaxed">
              From small-balance multifamily to value-add mixed-use, GroundUp Lending closes commercial real estate with the speed of
              private money and the structure of an institution. Bridge, bridge-to-perm, value-add, and acquisition capital — nationwide.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-px bg-white/15 border border-white/15 mb-12">
          {terms.map((t) => (
            <div key={t.v} className="bg-[#2C3E6D] p-6 md:p-8" data-testid={`commercial-term-${t.v.toLowerCase().replace(/ /g, "-")}`}>
              <div className="font-heading text-3xl md:text-4xl font-bold mb-2">{t.k}</div>
              <div className="label-overline text-white/60">{t.v}</div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/15 border border-white/15">
          {subtypes.map((s, i) => (
            <div
              key={s.name}
              className="bg-[#2C3E6D] p-6 md:p-8 hover:bg-[#22325A] transition-colors"
              data-testid={`commercial-subtype-${i}`}
            >
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-mono text-[#22C55E] text-sm">/{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-heading text-xl font-bold">{s.name}</h3>
              </div>
              <p className="text-white/70 text-[15px] leading-relaxed pl-9">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold mb-3">Programs we close on commercial deals:</h3>
            <ul className="space-y-2 text-white/85 text-[16px]">
              <li className="flex gap-3"><span className="text-[#22C55E] font-mono">→</span> Bridge & bridge-to-perm</li>
              <li className="flex gap-3"><span className="text-[#22C55E] font-mono">→</span> Value-add reposition (rehab + lease-up)</li>
              <li className="flex gap-3"><span className="text-[#22C55E] font-mono">→</span> Acquisition with rate-lock options</li>
              <li className="flex gap-3"><span className="text-[#22C55E] font-mono">→</span> Cash-out refinance on stabilized assets</li>
              <li className="flex gap-3"><span className="text-[#22C55E] font-mono">→</span> Construction completion & gap funding</li>
            </ul>
          </div>
          <div className="border border-white/15 p-8 md:p-10 bg-[#22325A]">
            <div className="label-overline text-[#22C55E] mb-4">Ready to underwrite</div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold mb-4">Send the deal. Term sheet in 48 hours.</h3>
            <p className="text-white/75 mb-6">
              Submit the OM, rent roll, and operating statements and we'll come back with indicative terms — no broker spread, no committee runaround.
            </p>
            <button onClick={onCtaClick} className="btn-primary" data-testid="commercial-cta">
              Submit Commercial Deal <ArrowUpRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const WhoWeServe = () => (
  <section id="who-we-serve" className="bg-[#0A0A0A] text-white py-24 md:py-32" data-testid="who-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
        <div className="md:col-span-7">
          <SectionLabel dark>02 — Who We Serve</SectionLabel>
          <h2 className="font-heading text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight">
            Built for operators
            <br />
            who don't have time to wait.
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-lg text-neutral-300 leading-relaxed">
            We work with new investors closing their first deal and seasoned operators running portfolios across multiple states.
            If you're moving real estate, we're built for you.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-neutral-800 border border-neutral-800">
        {WHO_WE_SERVE.map((w, i) => (
          <div
            key={w.title}
            className="bg-[#0A0A0A] p-8 md:p-10 hover:bg-[#171717] transition-colors"
            data-testid={`who-card-${i}`}
          >
            <div className="flex items-baseline gap-6 mb-4">
              <span className="font-mono text-[#22C55E] text-sm">/{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-heading text-2xl md:text-3xl font-bold">{w.title}</h3>
            </div>
            <p className="text-neutral-400 leading-relaxed pl-12">{w.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Process = () => (
  <section id="process" className="bg-[#F4F4F0] py-24 md:py-32" data-testid="process-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
        <div className="md:col-span-7">
          <SectionLabel>04 — Process</SectionLabel>
          <h2 className="font-heading text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight">
            From application
            <br />
            to funded — in days.
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-lg text-neutral-600 leading-relaxed">
            No 40-page applications. No mystery underwriting black box. A real human reviews your deal, prices it, and tells you
            yes or no — fast — so you can keep moving.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-4 border-t border-neutral-300">
        {PROCESS_STEPS.map((s) => (
          <div
            key={s.n}
            className="border-b md:border-b-0 md:border-r border-neutral-300 last:border-r-0 p-8 md:p-10 bg-white"
            data-testid={`process-step-${s.n}`}
          >
            <div className="font-heading text-7xl font-bold text-[#22C55E] mb-6">{s.n}</div>
            <h3 className="font-heading text-xl md:text-2xl font-bold mb-3">{s.title}</h3>
            <p className="text-neutral-600 text-[15px] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="about" className="bg-white py-24 md:py-32 border-y border-neutral-200" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-start">
      <div className="md:col-span-5">
        <div
          className="aspect-[4/5] w-full bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/8293654/pexels-photo-8293654.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          }}
        />
        <div className="grid grid-cols-2 mt-px gap-px bg-neutral-200">
          <div className="bg-[#0A0A0A] text-white p-6">
            <div className="label-overline text-neutral-400 mb-2">Coverage</div>
            <div className="font-heading text-3xl font-bold">All 50 States</div>
          </div>
          <div className="bg-[#22C55E] text-white p-6">
            <div className="label-overline text-white/70 mb-2">Underwriting</div>
            <div className="font-heading text-3xl font-bold">In-House</div>
          </div>
        </div>
      </div>
      <div className="md:col-span-7 md:pt-4">
        <SectionLabel>05 — About</SectionLabel>
        <h2 className="font-heading text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight">
          We started GroundUp Lending because every investor we knew was tired of slow lenders.
        </h2>
        <div className="mt-8 space-y-5 text-lg text-neutral-700 leading-relaxed">
          <p>
            GroundUp Lending is a nationwide private lender focused on one thing: getting real estate investors to the closing table — fast.
            We underwrite the asset, the plan, and the operator. Not just a credit score.
          </p>
          <p>
            Our team has funded fix & flips in suburban neighborhoods, ground-up new construction in growing metros, and value-add multifamily across the country.
            We've been on the borrower's side of the table — which is why our process feels different.
          </p>
          <p>
            One point of contact. Straight answers. Pricing that matches the deal. That's how lending should work — and how it works here.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
          {[
            { k: "Direct Lender", v: "We use our own capital — no middleman, no broker spread." },
            { k: "Investor-First", v: "Built by operators, for operators. We get the math." },
            { k: "Real Humans", v: "One file, one underwriter, one closer. Always reachable." },
          ].map((c) => (
            <div key={c.k} className="bg-white p-6">
              <div className="font-heading font-bold text-lg mb-2">{c.k}</div>
              <div className="text-sm text-neutral-600">{c.v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const FaqItem = ({ q, a, isOpen, onToggle, idx }) => (
  <div className="border-b border-neutral-300" data-testid={`faq-item-${idx}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-8 py-7 text-left"
      data-testid={`faq-toggle-${idx}`}
    >
      <span className="font-heading text-xl md:text-2xl font-semibold pr-4">{q}</span>
      <span className="w-10 h-10 flex items-center justify-center border border-neutral-400 flex-shrink-0">
        {isOpen ? <Minus size={18} /> : <Plus size={18} />}
      </span>
    </button>
    {isOpen && (
      <div className="pb-7 pr-16 text-[16px] text-neutral-600 leading-relaxed" data-testid={`faq-answer-${idx}`}>
        {a}
      </div>
    )}
  </div>
);

const FUNDED_GALLERY = [
  {
    img: "https://images.pexels.com/photos/1396132/pexels-photo-1396132.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    label: "Fix & Flip",
    loc: "Charlotte, NC",
    amount: "$285K",
  },
  {
    img: "https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    label: "New Construction",
    loc: "Austin, TX",
    amount: "$1.4M",
  },
  {
    img: "https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    label: "DSCR Rental",
    loc: "Tampa, FL",
    amount: "$420K",
  },
  {
    img: "https://images.pexels.com/photos/3935331/pexels-photo-3935331.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    label: "Fix & Flip",
    loc: "Phoenix, AZ",
    amount: "$340K",
  },
  {
    img: "https://images.pexels.com/photos/87223/pexels-photo-87223.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    label: "Multifamily 12-unit",
    loc: "Atlanta, GA",
    amount: "$3.2M",
  },
  {
    img: "https://images.pexels.com/photos/2462015/pexels-photo-2462015.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    label: "Bridge",
    loc: "Nashville, TN",
    amount: "$675K",
  },
];

const TESTIMONIALS = [
  {
    name: "Marcus T.",
    role: "Fix & Flip Investor",
    loc: "Charlotte, NC",
    avatar: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=300",
    quote:
      "GroundUp closed our 9-day flip with 100% of the rehab funded. Wires hit the table when they said they would. That alone is worth its weight in gold when contractors are waiting.",
  },
  {
    name: "Sarah K.",
    role: "Builder & Developer",
    loc: "Austin, TX",
    avatar: "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=300",
    quote:
      "I run three ground-up builds at a time. GroundUp's draw process is the cleanest I've used — submit photos, get released same week. My subs love it.",
  },
  {
    name: "James R.",
    role: "Buy-and-Hold Landlord",
    loc: "Tampa, FL",
    avatar: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=300",
    quote:
      "DSCR refi on four rentals in under three weeks. No tax returns, no W-2 hassle, locked in a great rate. They speak investor.",
  },
  {
    name: "Tina M.",
    role: "Wholesaler",
    loc: "Atlanta, GA",
    avatar: "https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=300",
    quote:
      "Same-day EMD and transactional funding on three back-to-back assignments. They're the only lender I keep on speed dial.",
  },
];

const FundedGallery = () => (
  <section className="bg-[#F4F4F0] pb-24 md:pb-32" data-testid="funded-gallery">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-end justify-between mb-10 gap-6 flex-wrap border-t border-neutral-300 pt-16">
        <div>
          <div className="label-overline text-neutral-500 mb-3 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-neutral-400" />
            Recently Funded
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight leading-[1.05]">
            Real deals. Real closings. <span className="text-[#22C55E]">Coast to coast.</span>
          </h2>
        </div>
        <p className="max-w-md text-neutral-600 text-[15px]">
          A small sample of recent projects funded by GroundUp Lending. Every closing represents an investor who picked up the phone — and got their capital fast.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-neutral-300 border border-neutral-300">
        {FUNDED_GALLERY.map((g, i) => (
          <div
            key={i}
            className="relative aspect-[4/3] bg-cover bg-center group overflow-hidden"
            style={{ backgroundImage: `url('${g.img}')` }}
            data-testid={`funded-${i}`}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/30 to-transparent" />
            <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-end text-white">
              <div className="label-overline text-[#22C55E] mb-1">{g.label}</div>
              <div className="font-heading text-2xl md:text-3xl font-bold leading-tight">{g.amount}</div>
              <div className="text-sm text-white/70 mt-1">{g.loc}</div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-neutral-500 mt-4 font-mono">* Representative project examples. Loan figures and locations illustrative.</p>
    </div>
  </section>
);

const Testimonials = () => (
  <section id="testimonials" className="bg-[#0A0A0A] text-white py-24 md:py-32 border-y border-neutral-900" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
        <div className="md:col-span-7">
          <SectionLabel dark>06 — What Investors Say</SectionLabel>
          <h2 className="font-heading text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight">
            Trusted by operators
            <br />
            <span className="text-[#22C55E]">closing deals every week.</span>
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-lg text-neutral-300 leading-relaxed">
            We measure success by closed deals, repeat borrowers, and referrals. Here's what some of the investors we work with have to say.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-neutral-800 border border-neutral-800">
        {TESTIMONIALS.map((t, i) => (
          <div key={t.name} className="bg-[#0A0A0A] p-8 md:p-10 flex flex-col" data-testid={`testimonial-${i}`}>
            <div className="font-heading text-5xl md:text-6xl text-[#22C55E] leading-none mb-4">"</div>
            <p className="text-lg md:text-xl leading-relaxed flex-1 text-neutral-200">{t.quote}</p>
            <div className="mt-8 pt-6 border-t border-neutral-800 flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-full bg-cover bg-center flex-shrink-0 border border-neutral-700"
                style={{ backgroundImage: `url('${t.avatar}')` }}
              />
              <div>
                <div className="font-heading font-bold text-base">{t.name}</div>
                <div className="text-sm text-neutral-400">
                  {t.role} · <span className="text-neutral-500">{t.loc}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-neutral-600 mt-6 font-mono">* Sample client experiences. Names anonymized. Individual results vary.</p>
    </div>
  </section>
);

const FAQsSection = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faqs" className="bg-[#F4F4F0] py-24 md:py-32" data-testid="faqs-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <SectionLabel>07 — FAQs</SectionLabel>
        <h2 className="font-heading text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight mb-12">
          Straight answers.
        </h2>
        <div className="border-t border-neutral-300">
          {FAQS.map((f, i) => (
            <FaqItem
              key={f.q}
              q={f.q}
              a={f.a}
              idx={i}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = ({ formRef, prefillAmount }) => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    loan_type: "Fix & Flip",
    loan_amount: "",
    property_address: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (prefillAmount) {
      setForm((f) => ({ ...f, loan_amount: prefillAmount }));
    }
  }, [prefillAmount]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("Application received. We'll be in touch within 24 hours.");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        loan_type: "Fix & Flip",
        loan_amount: "",
        property_address: "",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={formRef} className="bg-[#0A0A0A] text-white py-24 md:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionLabel dark>08 — Get Funded</SectionLabel>
          <h2 className="font-heading text-4xl md:text-6xl font-bold leading-[1.02] tracking-tight">
            Let's get your
            <br />
            <span className="text-[#22C55E]">next deal funded.</span>
          </h2>
          <p className="mt-6 text-lg text-neutral-300 leading-relaxed">
            Tell us about your deal and we'll come back with indicative terms within one business day. No soft-pull on credit. No spam.
          </p>
          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <Phone size={20} className="text-[#22C55E] mt-1" />
              <div>
                <div className="label-overline text-neutral-500 mb-1">Phone</div>
                <a href="tel:+19802023535" className="font-heading text-xl hover:text-[#22C55E] transition-colors">
                  (980) 202-3535
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail size={20} className="text-[#22C55E] mt-1" />
              <div>
                <div className="label-overline text-neutral-500 mb-1">Email</div>
                <a href="mailto:newbuild@grounduplending.com" className="font-heading text-xl hover:text-[#22C55E] transition-colors">
                  newbuild@grounduplending.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin size={20} className="text-[#22C55E] mt-1" />
              <div>
                <div className="label-overline text-neutral-500 mb-1">Coverage</div>
                <div className="font-heading text-xl">Nationwide · All 50 States</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div className="border border-neutral-800 p-10 md:p-14 bg-[#171717]" data-testid="contact-success">
              <CheckCircle2 size={40} className="text-[#22C55E] mb-6" />
              <h3 className="font-heading text-3xl md:text-4xl font-bold mb-4">Application received.</h3>
              <p className="text-neutral-300 text-lg mb-8 leading-relaxed">
                A real underwriter from GroundUp Lending will reach out within one business day with next steps. Watch your inbox and phone.
              </p>
              <button onClick={() => setSubmitted(false)} className="btn-ghost" data-testid="contact-reset">
                Submit Another Deal <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="border border-neutral-800 p-8 md:p-12 bg-[#171717] space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-overline text-neutral-400 mb-2 block">Full Name *</label>
                  <input
                    name="full_name"
                    value={form.full_name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Jane Investor"
                    data-testid="form-full-name"
                  />
                </div>
                <div>
                  <label className="label-overline text-neutral-400 mb-2 block">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="jane@portfolio.com"
                    data-testid="form-email"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-overline text-neutral-400 mb-2 block">Phone *</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="(555) 555-1234"
                    data-testid="form-phone"
                  />
                </div>
                <div>
                  <label className="label-overline text-neutral-400 mb-2 block">Loan Type</label>
                  <select
                    name="loan_type"
                    value={form.loan_type}
                    onChange={handleChange}
                    className="form-input"
                    data-testid="form-loan-type"
                  >
                    {PRODUCTS.map((p) => (
                      <option key={p.id} value={p.name} className="bg-[#171717]">
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="label-overline text-neutral-400 mb-2 block">Estimated Loan Amount</label>
                  <input
                    name="loan_amount"
                    value={form.loan_amount}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="$250,000"
                    data-testid="form-loan-amount"
                  />
                </div>
                <div>
                  <label className="label-overline text-neutral-400 mb-2 block">Property Address</label>
                  <input
                    name="property_address"
                    value={form.property_address}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="123 Main St, City, ST"
                    data-testid="form-property"
                  />
                </div>
              </div>
              <div>
                <label className="label-overline text-neutral-400 mb-2 block">Deal Details</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  className="form-input resize-none"
                  placeholder="Brief overview of the deal — strategy, timeline, ARV, etc."
                  data-testid="form-message"
                />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="form-submit">
                {submitting ? "Submitting..." : "Submit Application"} <ArrowUpRight size={18} />
              </button>
              <p className="text-xs text-neutral-500 text-center">
                No soft credit pull. By submitting you agree to be contacted about your inquiry.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-[#0A0A0A] text-white border-t border-neutral-800 pt-24 pb-10" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-10 mb-20">
        <div className="md:col-span-6">
          <div className="flex items-center gap-3 mb-6">
            <img src="/brand/groundup-logo-light.png" alt="GroundUp Lending" className="h-12 w-auto" />
          </div>
          <p className="font-heading text-3xl md:text-5xl font-bold leading-tight max-w-2xl">
            Capital, built from the <span className="text-[#22C55E]">ground up.</span>
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="label-overline text-neutral-500 mb-4">Products</div>
          <ul className="space-y-3">
            {PRODUCTS.map((p) => (
              <li key={p.id}>
                <a href="#products" className="hover:text-[#22C55E] transition-colors">
                  {p.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="label-overline text-neutral-500 mb-4">Company</div>
          <ul className="space-y-3">
            <li><a href="#about" className="hover:text-[#22C55E] transition-colors">About</a></li>
            <li><a href="#process" className="hover:text-[#22C55E] transition-colors">Process</a></li>
            <li><a href="#faqs" className="hover:text-[#22C55E] transition-colors">FAQs</a></li>
            <li><a href="/brokers" className="hover:text-[#22C55E] transition-colors">Brokers</a></li>
            <li><a href="#contact" className="hover:text-[#22C55E] transition-colors">Contact</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-neutral-800 pt-8 flex flex-col md:flex-row justify-between gap-4 text-sm text-neutral-500">
        <div>© {new Date().getFullYear()} GroundUp Lending. All rights reserved.</div>
        <div className="font-mono">Nationwide Private Lending · Equal Opportunity Lender</div>
      </div>
    </div>
  </footer>
);

const Home = () => {
  const formRef = useRef(null);
  const [prefillAmount, setPrefillAmount] = useState("");

  const scrollToContact = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLockTerms = (amount) => {
    setPrefillAmount(amount);
    // small delay so prefill applies before scroll
    setTimeout(scrollToContact, 50);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("in-view");
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-[#F4F4F0] min-h-screen" data-testid="home-page">
      <Nav onCtaClick={scrollToContact} />
      <Hero onCtaClick={scrollToContact} onLockTerms={handleLockTerms} />
      <TrustMarquee />
      <Products onCtaClick={scrollToContact} />
      <Commercial onCtaClick={scrollToContact} />
      <WhoWeServe />
      <Process />
      <FundedGallery />
      <About />
      <Testimonials />
      <FAQsSection />
      <Contact formRef={formRef} prefillAmount={prefillAmount} />
      <Footer />
    </div>
  );
};

export default Home;
